import React, { useState, useCallback, useRef, useEffect } from 'react';
import './VisualTester.css';
import { TEST_REGISTRY } from './testRegistry';
import {
  runAllTests,
  runTestSuite,
  runSingleTest,
  initTestStatuses,
  getSuiteStats,
  TEST_STATUS,
} from './testRunner';
import { setApiMode } from './apiMode';

function revokeSnapshot() {
  // snapshots are HTML strings — nothing to revoke
}

export default function VisualTesterPage() {
  const [statuses, setStatuses] = useState(() => initTestStatuses(TEST_REGISTRY));
  const [isRunning, setIsRunning] = useState(false);
  const [activeSuite, setActiveSuite] = useState('all');
  const [collapsed, setCollapsed] = useState({});
  const [expanded, setExpanded] = useState({});
  const [apiMode, setMode] = useState('mock');
  const runningRef = useRef(false);

  useEffect(() => {
    setApiMode('mock');
  }, []);

  const handleSetMode = (mode) => {
    setMode(mode);
    setApiMode(mode);
  };

  const updateStatus = useCallback((testId, status, error, duration, snapshot, logs) => {
    setStatuses(prev => {
      revokeSnapshot(prev[testId]);
      return {
        ...prev,
        [testId]: { status, error, duration, snapshot: snapshot || null, logs: logs || [] },
      };
    });
  }, []);

  const handleRunAll = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setIsRunning(true);
    setStatuses(prev => {
      Object.values(prev).forEach(revokeSnapshot);
      return initTestStatuses(TEST_REGISTRY);
    });
    try {
      await runAllTests(TEST_REGISTRY, updateStatus);
    } finally {
      runningRef.current = false;
      setIsRunning(false);
    }
  };

  const handleRunTest = async (test, e) => {
    if (e) e.stopPropagation();
    if (runningRef.current) return;
    runningRef.current = true;
    setIsRunning(true);
    setStatuses(prev => {
      revokeSnapshot(prev[test.id]);
      return {
        ...prev,
        [test.id]: { status: TEST_STATUS.IDLE, error: null, duration: null, snapshot: null, logs: [] },
      };
    });
    try {
      await runSingleTest(test, updateStatus);
    } finally {
      runningRef.current = false;
      setIsRunning(false);
    }
  };

  const handleRunSuite = async (suite, e) => {
    if (e) e.stopPropagation();
    if (runningRef.current) return;
    runningRef.current = true;
    setIsRunning(true);
    setStatuses(prev => {
      const next = { ...prev };
      suite.tests.forEach(t => {
        revokeSnapshot(prev[t.id]);
        next[t.id] = { status: TEST_STATUS.IDLE, error: null, duration: null, snapshot: null, logs: [] };
      });
      return next;
    });
    try {
      await runTestSuite(suite, updateStatus);
    } finally {
      runningRef.current = false;
      setIsRunning(false);
    }
  };

  const toggleExpand = (testId, hasDetail) => {
    if (!hasDetail) return;
    setExpanded(prev => ({ ...prev, [testId]: !prev[testId] }));
  };

  const totalTests = TEST_REGISTRY.reduce((s, suite) => s + suite.tests.length, 0);
  const passedTotal = Object.values(statuses).filter(s => s.status === TEST_STATUS.PASSED).length;
  const failedTotal = Object.values(statuses).filter(s => s.status === TEST_STATUS.FAILED).length;
  const runTotal = passedTotal + failedTotal;
  const progress = totalTests > 0 ? Math.round((runTotal / totalTests) * 100) : 0;

  const visibleSuites = activeSuite === 'all'
    ? TEST_REGISTRY
    : TEST_REGISTRY.filter(s => s.id === activeSuite);

  return (
    <div className="vt-root">
      {/* Sidebar */}
      <div className="vt-sidebar">
        <div className="vt-sidebar-header">
          <p className="vt-sidebar-title">Visual Tester</p>
          <p className="vt-sidebar-subtitle">AIQDA Integration Tests</p>
        </div>

        <button className="vt-run-all-btn" onClick={handleRunAll} disabled={isRunning}>
          {isRunning ? '⏳ Running…' : '▶ Run All Tests'}
        </button>

        <div className="vt-mode-toggle">
          <button
            className={`vt-mode-btn${apiMode === 'mock' ? ' active' : ''}`}
            onClick={() => handleSetMode('mock')}
            title="Fast — no backend required"
          >
            Mock
          </button>
          <button
            className={`vt-mode-btn vt-mode-btn-real${apiMode === 'real' ? ' active' : ''}`}
            onClick={() => handleSetMode('real')}
            title="Calls real backend endpoints"
          >
            Real APIs
          </button>
        </div>

        <nav className="vt-suite-nav">
          <div
            className={`vt-suite-nav-item ${activeSuite === 'all' ? 'active' : ''}`}
            onClick={() => setActiveSuite('all')}
          >
            <span className="vt-suite-icon">🗂️</span>
            <span className="vt-suite-nav-label">All Suites</span>
            <span className={`vt-suite-badge ${
              failedTotal > 0 ? 'has-fail'
                : passedTotal === totalTests && passedTotal > 0 ? 'all-pass'
                  : 'idle'
            }`}>
              {passedTotal}/{totalTests}
            </span>
          </div>

          {TEST_REGISTRY.map(suite => {
            const stats = getSuiteStats(suite, statuses);
            const badgeClass =
              stats.running > 0 ? 'running'
                : stats.failed > 0 ? 'has-fail'
                  : stats.passed === stats.total && stats.passed > 0 ? 'all-pass'
                    : 'idle';
            return (
              <div
                key={suite.id}
                className={`vt-suite-nav-item ${activeSuite === suite.id ? 'active' : ''}`}
                onClick={() => setActiveSuite(suite.id)}
              >
                <span className="vt-suite-icon">{suite.icon}</span>
                <span className="vt-suite-nav-label">{suite.name}</span>
                <span className={`vt-suite-badge ${badgeClass}`}>
                  {stats.passed}/{stats.total}
                </span>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div className="vt-content">
        <div className="vt-content-header">
          <h1 className="vt-content-title">
            {activeSuite === 'all'
              ? 'All Test Suites'
              : TEST_REGISTRY.find(s => s.id === activeSuite)?.name ?? 'Tests'}
          </h1>
          <div className="vt-suite-stats">
            <span className="vt-stat-pass">{passedTotal} passed</span>
            <span>·</span>
            <span className="vt-stat-fail">{failedTotal} failed</span>
            <span>·</span>
            <span>{totalTests - runTotal} pending</span>
          </div>
        </div>

        <div className="vt-progress-bar">
          <div className="vt-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="vt-test-list">
          {visibleSuites.map(suite => {
            const stats = getSuiteStats(suite, statuses);
            const isCollapsed = collapsed[suite.id];
            return (
              <div key={suite.id} className="vt-suite-section">
                <div
                  className="vt-suite-section-header"
                  onClick={() =>
                    setCollapsed(prev => ({ ...prev, [suite.id]: !prev[suite.id] }))
                  }
                >
                  <span className="vt-suite-section-icon">{suite.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div className="vt-suite-section-name">{suite.name}</div>
                    <div className="vt-suite-section-desc">{suite.description}</div>
                  </div>
                  <div className="vt-suite-stats" style={{ marginRight: 8 }}>
                    <span className="vt-stat-pass">{stats.passed}✓</span>
                    {stats.failed > 0 && (
                      <span className="vt-stat-fail">{stats.failed}✗</span>
                    )}
                    <span>{stats.total} tests</span>
                  </div>
                  <button
                    className="vt-suite-run-btn"
                    onClick={e => handleRunSuite(suite, e)}
                    disabled={isRunning}
                  >
                    Run
                  </button>
                  <span style={{ marginLeft: 6, color: '#9ca3af', fontSize: 12 }}>
                    {isCollapsed ? '▶' : '▼'}
                  </span>
                </div>

                {!isCollapsed &&
                  suite.tests.map(test => {
                    const s = statuses[test.id] || {
                      status: TEST_STATUS.IDLE,
                      error: null,
                      duration: null,
                      snapshot: null,
                      logs: [],
                    };
                    const hasDetail = !!(s.error || s.snapshot || s.logs?.length);
                    const isExpanded = expanded[test.id];
                    return (
                      <div
                        key={test.id}
                        className={`vt-test-item${isExpanded ? ' expanded' : ''}`}
                        data-testid={`test-item-${test.id}`}
                        data-status={s.status}
                      >
                        <div
                          className="vt-test-item-header"
                          onClick={() => toggleExpand(test.id, hasDetail)}
                          style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                        >
                          <div className={`vt-status-dot ${s.status}`} />
                          <div className="vt-test-info">
                            <div className="vt-test-name">{test.name}</div>
                          </div>
                          {s.duration !== null && (
                            <div className="vt-test-duration">{s.duration}ms</div>
                          )}
                          {hasDetail && (
                            <span className="vt-expand-chevron">
                              {isExpanded ? '▲' : '▼'}
                            </span>
                          )}
                          <button
                            className="vt-test-run-btn"
                            onClick={e => handleRunTest(test, e)}
                            disabled={isRunning}
                            title="Run this test"
                            data-testid={`run-btn-${test.id}`}
                          >▶</button>
                        </div>

                        {isExpanded && (
                          <div className="vt-test-detail">
                            {s.error && (
                              <div className="vt-detail-section">
                                <div className="vt-detail-label">Error</div>
                                <pre className="vt-error-pre">{s.error}</pre>
                              </div>
                            )}
                            {s.logs?.length > 0 && (
                              <div className="vt-detail-section">
                                <div className="vt-detail-label">Console ({s.logs.length})</div>
                                <div className="vt-log-list">
                                  {s.logs.map((log, i) => (
                                    <div key={i} className={`vt-log-entry vt-log-${log.level}`}>
                                      <span className="vt-log-level">{log.level}</span>
                                      <span className="vt-log-msg">{log.msg}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {s.snapshot && (
                              <div className="vt-detail-section">
                                <div className="vt-detail-label">Rendered Output</div>
                                <iframe
                                  srcDoc={s.snapshot}
                                  className="vt-snapshot-iframe"
                                  title={`Preview: ${test.name}`}
                                  onLoad={e => {
                                    try {
                                      const doc = e.target.contentDocument || e.target.contentWindow?.document;
                                      if (doc?.body) {
                                        const h = Math.min(Math.max(doc.body.scrollHeight + 24, 80), 520);
                                        e.target.style.height = `${h}px`;
                                      }
                                    } catch {}
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>

        <div className="vt-footer">
          <div className="vt-footer-stat">
            Total: <strong>{totalTests}</strong>
          </div>
          <div className="vt-footer-pass">✓ {passedTotal} passed</div>
          {failedTotal > 0 && (
            <div className="vt-footer-fail">✗ {failedTotal} failed</div>
          )}
          <div className="vt-footer-right">{progress}% complete</div>
        </div>
      </div>
    </div>
  );
}
